import type {
  ComboboxData,
  ComboboxItem,
  ComboboxItemGroup,
} from "@mantine/core";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import {
  Group,
  Radio,
  RadioGroup,
  type RadioGroupProps,
  Stack,
} from "@/components/ui/elements";

type Props<FORM_TYPE extends FieldValues> = {
  control: Control<FORM_TYPE>;
  name: FieldPath<FORM_TYPE>;
  data: ComboboxData;
  row?: boolean;
  disabled?: boolean;
  // 呼び出し元が別の場所にエラーを出す場合に、二重表示を避けるための指定
  hideError?: boolean;
} & Omit<RadioGroupProps, "name" | "children">;

export function RadioField<FORM_TYPE extends FieldValues>({
  control,
  name,
  data,
  row,
  disabled,
  hideError = false,
  ...radioProps
}: Props<FORM_TYPE>) {
  const RadioWrapper = ({ children }: { children: React.ReactNode }) =>
    row ? <Group>{children}</Group> : <Stack>{children}</Stack>;

  const renderRadio = (
    item: string | ComboboxItem | ComboboxItemGroup<string | ComboboxItem>,
    i: string | number,
  ) => {
    if (typeof item === "string") {
      return <Radio key={i} label={item} value={item} disabled={disabled} />;
    } else if ("label" in item && "value" in item) {
      return (
        <Radio
          key={i}
          label={item.label}
          value={item.value}
          disabled={disabled}
        />
      );
    } else if ("group" in item && "items" in item) {
      return renderRadioGroup(item, i);
    }
  };

  const renderRadioGroup = (
    item: { group: string; items: (string | ComboboxItem)[] },
    i: string | number,
  ) => (
    <div key={i}>
      <strong>{item.group}</strong>
      {item.items.map((groupItem, groupIndex) =>
        renderRadio(groupItem, `${i}-${groupIndex}`),
      )}
    </div>
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <RadioGroup
          {...field}
          {...radioProps}
          value={field.value ?? undefined}
          error={hideError ? undefined : error?.message}
        >
          <RadioWrapper>
            {data.map((item, i) => renderRadio(item, i))}
          </RadioWrapper>
        </RadioGroup>
      )}
    />
  );
}
